/**
 * Phase 45.1b — dashboard wrappers for the enterprise SSO surface.
 *
 * `discover` + `exchange` are the public login-flow endpoints; the
 * `/orgs/:orgId/sso` trio is the admin configuration surface gated on
 * `auth.configure_sso`. The IdP redirect (`/auth/sso/connections/:id/start`)
 * is a browser navigation, not an XHR — see `ssoStartUrl` in
 * pages/auth/ssoNavigation.ts.
 *
 * Adapter over `@commerce-os/api-client`'s `ssoApi` (the shared contract),
 * bound to the dashboard's axios instance — same shape as `kdsApi.ts`.
 * The export shape is unchanged for the hooks above it.
 */
import { ssoApi as shared } from '@commerce-os/api-client';
import type {
  CreateScimTokenRequest,
  CreateScimTokenResponse,
  DeleteSsoConnectionResponse,
  RevokeScimTokenResponse,
  ScimTokenListResponse,
  SsoConnectionResponse,
  SsoDiscoverResponse,
  SsoExchangeResponse,
  SsoTrustView,
  UpdateSsoTrustDelegatesRequest,
  UpsertSsoConnectionRequest,
} from '@commerce-os/shared-types';
import { getApiClient } from '../lib/axios';

export const ssoApi = {
  /** Login-page IdP discovery — domain-level, no user enumeration. */
  discover: (email: string): Promise<SsoDiscoverResponse> =>
    shared.discoverSso(getApiClient(), email),

  /** One-time exchange code → normal token pair (single-use, 60s TTL). */
  exchange: (token: string): Promise<SsoExchangeResponse> =>
    shared.exchangeSsoCode(getApiClient(), token),

  get: (orgId: string): Promise<SsoConnectionResponse> =>
    shared.getSsoConnection(getApiClient(), orgId),

  upsert: (orgId: string, body: UpsertSsoConnectionRequest): Promise<SsoConnectionResponse> =>
    shared.upsertSsoConnection(getApiClient(), orgId, body),

  remove: (orgId: string): Promise<DeleteSsoConnectionResponse> =>
    shared.deleteSsoConnection(getApiClient(), orgId),

  /** Phase 67 hotfix (question 116) — owner-only: who else may change the
   *  SSO trust settings. The full list; resolves to the trust view. */
  updateTrustDelegates: (
    orgId: string,
    body: UpdateSsoTrustDelegatesRequest,
  ): Promise<SsoTrustView> => shared.updateSsoTrustDelegates(getApiClient(), orgId, body),

  // ── SCIM provisioning tokens (Phase 45.2b) ──────────────────────────────

  /** Mint a token — the response's `token` is the raw bearer secret,
   *  shown exactly once and never retrievable again. */
  createScimToken: (
    orgId: string,
    body: CreateScimTokenRequest,
  ): Promise<CreateScimTokenResponse> => shared.createScimToken(getApiClient(), orgId, body),

  listScimTokens: (orgId: string): Promise<ScimTokenListResponse> =>
    shared.listScimTokens(getApiClient(), orgId),

  revokeScimToken: (orgId: string, tokenId: string): Promise<RevokeScimTokenResponse> =>
    shared.revokeScimToken(getApiClient(), orgId, tokenId),
};
